import {v3} from "../short3.js";
import Debugs from "../Debugs.js";
import Textures from "../Textures.js";
import Cone from "../math/Cone.js";
import AbstractAxisIntersectionHandler from "./AbstractAxisIntersectionHandler.js";

//statische Hilfsvariablen, die in isIntersectingTriangle benutzt werden,
//aber aus Performancegründen nicht bei jedem Methodenaufruf neu erzeugt werden

const diff0 = v3();
const diff1 = v3();
const diff2 = v3();
const edge0 = v3();
const edge1 = v3();
const kE2 = v3();
const N = v3();
const U = v3();
const NcU = v3();
const NdaMdiff0 = v3();

/**
 * Ermittelt alle Schnittpunkte von Meshes mit einem Kegel-Volumen (der Kegel muss unendlich lang sein).
 * http://preview.objectcode.de/HoloExamples/#oc-threejs.TestIntersectionHandler
 * @example
 * //Die Schnitt-Ergebnisse liegen in der folgenden Form vor:
 * {
 *      point, // der Schnittpunkt (geht immer durch die Kegel-Achse)
 *      distance, // der Abstand point zum Kegel.origin (die Spitze)
 *      object, // das Mesh-Objekt
 *      rayIsInTriangle, // schneidet die Kegel-Achse das Object, oder nur das Volumen?
 *      face, // das Dreieck
 *      faceIndex, // Index des Dreiecks
 * }
 * //Typischer Aufruf:
 * var handler = new ConeIntersectionHandler(objects,{cone: new Cone(v3(0,0,0),v3(0,0,1),5*Math.PI/180,Number.POSITIVE_INFINITY)});
 * handler.intersect(intersection => console.log(intersection));
 * // Ergebnis testweise visualisieren
 * handler.debugShow(true);
 */
export default class ConeIntersectionHandler extends AbstractAxisIntersectionHandler {

    constructor(objects,options) {
        super(objects,options);

        this.coneInWorldSpace = (options && options.cone) || new Cone();
        this.cone = this.coneInWorldSpace.clone();

        if (Number.isFinite(this.coneInWorldSpace.length)) {
            throw "ConeIntersectionHandler only accepts infinite cones";
        }

    }

    debugShow (showCone = false) {
        if (showCone) {
            Textures.setOpacity(Debugs.cone(this.coneInWorldSpace, 0xa0a0a0),0.3);
        }
        this.intersect(x => Debugs.point(x.point));
    }

    getGeometryInLocalSpace() {
        return this.cone;
    }

    getGeometryInWorldSpace() {
        return this.coneInWorldSpace;
    }

    isIntersectingTriangle (p0, p1, p2) {
        
        const cone = this.cone;
        
        // see https://github.com/timprepscius/GeometricTools/blob/master/WildMagic5/LibMathematics/Intersection/Wm5IntrTriangle3Cone3.cpp
        // Triangle is <P0,P1,P2>, edges are E0 = P1-P0, E1=P2-P0.
        let onConeSide = 0;
        let p0Test = 0;
        let p1Test = 0;
        let p2Test = 0;
        let AdE, EdE, EdD, c1, c2;

        const cosSqr = Math.pow(cone.cosAlpha, 2);

        // Test vertex P0.
        diff0.subVectors(p0, cone.origin);
        const AdD0 = cone.direction.dot(diff0);
        if (AdD0 >= 0) {
            // P0 is on cone side of plane.
            p0Test = AdD0 * AdD0 - cosSqr * diff0.dot(diff0);
            if (p0Test >= 0) {
                // P0 is inside the cone.
                return true;
            } else {
                // P0 is outside the cone, but on cone side of plane.
                onConeSide |= 1;
            }
        }
        // else P0 is not on cone side of plane.

        // Test vertex P1.
        edge0.subVectors(p1, p0);
        diff1.addVectors(diff0, edge0);
        const AdD1 = cone.direction.dot(diff1);
        if (AdD1 >= 0) {
            // P1 is on cone side of plane.
            p1Test = AdD1 * AdD1 - cosSqr * diff1.dot(diff1);
            if (p1Test >= 0) {
                // P1 is inside the cone.
                return true;
            } else {
                // P1 is outside the cone, but on cone side of plane.
                onConeSide |= 2;
            }
        }
        // else P1 is not on cone side of plane.

        // Test vertex P2.
        edge1.subVectors(p2, p0);
        diff2.addVectors(diff0, edge1);
        const AdD2 = cone.direction.dot(diff2);
        if (AdD2 >= 0) {
            // P2 is on cone side of plane.
            p2Test = AdD2 * AdD2 - cosSqr * diff2.dot(diff2);
            if (p2Test >= 0) {
                // P2 is inside the cone.
                return true;
            } else {
                // P2 is outside the cone, but on cone side of plane.
                onConeSide |= 4;
            }
        }
        // else P2 is not on cone side of plane.

        // Test edge <P0,P1> = E0.
        if (onConeSide & 3) {
            AdE = AdD1 - AdD0;
            EdE = edge0.dot(edge0);
            c2 = AdE * AdE - cosSqr * EdE;
            if (c2 < 0) {
                EdD = edge0.dot(diff0);
                c1 = AdE * AdD0 - cosSqr * EdD;
                if (onConeSide & 1) {
                    if (onConeSide & 2) {
                        // <P0,P1> fully on cone side of plane, fC0 = p0Test.
                        if (0 <= c1 && c1 <= -c2 && c1 * c1 >= p0Test * c2) {
                            return true;
                        }
                    } else {
                        // P0 on cone side (Dot(A,P0-V) >= 0),
                        // P1 on opposite side (Dot(A,P1-V) <= 0)
                        // (Dot(A,E0) <= 0), fC0 = p0Test.
                        if (0 <= c1 && c2 * AdD0 <= c1 * AdE && c1 * c1 >= p0Test * c2) {
                            return true;
                        }
                    }
                } else {
                    // P1 on cone side (Dot(A,P1-V) >= 0),
                    // P0 on opposite side (Dot(A,P0-V) <= 0)
                    // (Dot(A,E0) >= 0), fC0 = p0Test (needs calculating).
                    if (c1 <= -c2 && c2 * AdD0 <= c1 * AdE) {
                        p0Test = AdD0 * AdD0 - cosSqr * diff0.dot(diff0);
                        if (c1 * c1 >= p0Test * c2) {
                            return true;
                        }
                    }
                }
            }
        }
        // else <P0,P1> does not intersect cone half space.

        // Test edge <P0,P2> = E1.
        if (onConeSide & 5) {
            AdE = AdD2 - AdD0;
            EdE = edge1.dot(edge1);
            c2 = AdE * AdE - cosSqr * EdE;
            if (c2 < 0) {
                EdD = edge1.dot(diff0);
                c1 = AdE * AdD0 - cosSqr * EdD;
                if (onConeSide & 1) {
                    if (onConeSide & 4) {
                        // <P0,P2> fully on cone side of plane, fC0 = p0Test.
                        if (0.0 <= c1 && c1 <= -c2 && c1 * c1 >= p0Test * c2) {
                            return true;
                        }
                    } else {
                        // P0 on cone side (Dot(A,P0-V) >= 0),
                        // P2 on opposite side (Dot(A,P2-V) <= 0)
                        // (Dot(A,E1) <= 0), fC0 = p0Test.
                        if (0.0 <= c1 && c2 * AdD0 <= c1 * AdE && c1 * c1 >= p0Test * c2) {
                            return true;
                        }
                    }
                } else {
                    // P2 on cone side (Dot(A,P2-V) >= 0),
                    // P0 on opposite side (Dot(A,P0-V) <= 0)
                    // (Dot(A,E1) >= 0), fC0 = p0Test (needs calculating).
                    if (c1 <= -c2 && c2 * AdD0 <= c1 * AdE) {
                        p0Test = AdD0 * AdD0 - cosSqr * diff0.dot(diff0);
                        if (c1 * c1 >= p0Test * c2) {
                            return true;
                        }
                    }
                }
            }
        }
        // else <P0,P2> does not intersect cone half space.

        // Test edge <P1,P2> = E1-E0 = E2.
        if (onConeSide & 6) {
            kE2.subVectors(edge1, edge0);
            AdE = AdD2 - AdD1;
            EdE = kE2.dot(kE2);
            c2 = AdE * AdE - cosSqr * EdE;
            if (c2 < 0.0) {
                EdD = kE2.dot(diff1);
                c1 = AdE * AdD1 - cosSqr * EdD;
                if (onConeSide & 2) {
                    if (onConeSide & 4) {
                        // <P1,P2> fully on cone side of plane, fC0 = p1Test.
                        if (0.0 <= c1 && c1 <= -c2 && c1 * c1 >= p1Test * c2) {
                            return true;
                        }
                    } else {
                        // P1 on cone side (Dot(A,P1-V) >= 0),
                        // P2 on opposite side (Dot(A,P2-V) <= 0)
                        // (Dot(A,E2) <= 0), fC0 = p1Test.
                        if (0.0 <= c1 && c2 * AdD1 <= c1 * AdE
                            && c1 * c1 >= p1Test * c2) {
                            return true;
                        }
                    }
                } else {
                    // P2 on cone side (Dot(A,P2-V) >= 0),
                    // P1 on opposite side (Dot(A,P1-V) <= 0)
                    // (Dot(A,E2) >= 0), fC0 = p1Test (needs calculating).
                    if (c1 <= -c2 && c2 * AdD1 <= c1 * AdE) {
                        p1Test = AdD1 * AdD1 - cosSqr * diff1.dot(diff1);
                        if (c1 * c1 >= p1Test * c2) {
                            return true;
                        }
                    }
                }
            }
        }
        // else <P1,P2> does not intersect cone half space.

        // Test triangle <P0,P1,P2>.  It is enough to handle only the case when
        // at least one Pi is on the cone side of the plane.  In this case and
        // after the previous testing, if the triangle intersects the cone, the
        // set of intersection must contain the point of intersection between
        // the cone axis and the triangle.
        if (onConeSide > 0) {
            N.crossVectors(edge0, edge1);
            const NdA = N.dot(cone.direction);
            const NdD = N.dot(diff0);
            U.copy(cone.direction).multiplyScalar(NdD).sub(NdaMdiff0.copy(diff0).multiplyScalar(NdA));
            NcU.crossVectors(N, U);

            const NcUdE0 = NcU.dot(edge0);
            if (NdA >= 0) {
                if (NcUdE0 <= 0) {
                    var NcUdE1 = NcU.dot(edge1);
                    if (NcUdE1 >= 0) {
                        var NcUdE2 = NcUdE1 - NcUdE0;
                        var NdN = N.lengthSq();
                        if (NcUdE2 <= NdA * NdN) {
                            return true;
                        }
                    }
                }
            } else {
                if (NcUdE0 >= 0) {
                    var NcUdE1 = NcU.dot(edge1);
                    if (NcUdE1 <= 0) {
                        var NcUdE2 = NcUdE1 - NcUdE0;
                        var NdN = N.lengthSq();
                        if (NcUdE2 >= NdA * NdN) {
                            return true;
                        }
                    }
                }
            }
        }

        return false;
    }

}
